import React, { useState, useEffect, useCallback } from "react";
import { UserPlus, Trash2, Loader2, KeyRound, ShieldCheck, User, Mail, Phone } from "lucide-react";
import { listUsers, createUser, deleteUser, getLanguages } from "../lib/api";
import { toast } from "sonner";

export default function UsersAdmin({ session }) {
  const [users, setUsers] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("user");
  const [gender, setGender] = useState("male");
  const [uiLang, setUiLang] = useState("gu");
  const [saving, setSaving] = useState(false);
  const langName = (code) => {
    const l = languages.find((x) => x.code === code);
    return l ? l.english : (code || "Gujarati");
  };

  const refresh = useCallback(async () => {
    try {
      setUsers(await listUsers(session.user_id));
    } catch (e) { /* ignore */ }
  }, [session.user_id]);

  useEffect(() => { refresh(); getLanguages().then(setLanguages).catch(() => {}); }, []); // eslint-disable-line

  const save = async () => {
    if (!name.trim() || !pin.trim()) { toast.error("Enter a name and PIN"); return; }
    setSaving(true);
    try {
      await createUser(session.user_id, { name, pin, role, gender, ui_lang: uiLang, email, phone });
      toast.success("Login created");
      setName(""); setPin(""); setEmail(""); setPhone(""); setRole("user"); setGender("male"); setUiLang("gu");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create login");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    try {
      await deleteUser(session.user_id, u.id);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="users-admin">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#1A1C19] text-[#FFD600] flex items-center justify-center border-3 border-[#1A1C19]">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <div>
          <h2 className="yb-heading text-2xl font-extrabold">Access & Logins</h2>
          <p className="text-sm text-[#50564F]">Create and manage PIN logins</p>
        </div>
      </div>

      {/* Create form */}
      <div className="bg-[#FEFCE8] rounded-2xl border-3 border-[#1A1C19] p-4 space-y-3" style={{ boxShadow: "0 4px 0 0 #1A1C19" }}>
        <h3 className="yb-heading font-extrabold text-lg flex items-center gap-2"><UserPlus className="w-5 h-5" /> New login</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="user-name-input"
            className="yb-tactile bg-white px-4 py-3 outline-none" />
          <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="PIN (numbers only)" inputMode="numeric" data-testid="user-pin-input"
            className="yb-tactile bg-white px-4 py-3 outline-none" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="user-email-input"
            className="yb-tactile bg-white px-4 py-3 outline-none" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" data-testid="user-phone-input"
            className="yb-tactile bg-white px-4 py-3 outline-none" />
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="user-role-select" className="yb-tactile bg-white px-4 py-3 font-bold">
            <option value="user">User (scanner only)</option>
            <option value="master">Master (full access)</option>
          </select>
          <select value={gender} onChange={(e) => setGender(e.target.value)} data-testid="user-gender-select" className="yb-tactile bg-white px-4 py-3 font-bold">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <select value={uiLang} onChange={(e) => setUiLang(e.target.value)} data-testid="user-lang-select" className="yb-tactile bg-white px-4 py-3 font-bold sm:col-span-2">
            {languages.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.english})</option>)}
          </select>
        </div>
        <button onClick={save} disabled={saving} data-testid="user-save" className="yb-tactile w-full py-3 bg-[#15803D] text-white font-bold flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />} Create Login
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        <h3 className="yb-heading font-extrabold text-lg">All logins ({users.length})</h3>
        {users.map((u) => (
          <div key={u.id} className="yb-tactile bg-white p-4 flex items-center gap-3" data-testid={`user-row-${u.id}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#1A1C19] shrink-0 ${u.role === "master" ? "bg-[#1A1C19] text-[#FFD600]" : "bg-[#FFD600]"}`}>
              {u.role === "master" ? <ShieldCheck className="w-5 h-5" /> : <User className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{u.name} {u.is_default && <span className="text-xs text-[#50564F]">(default)</span>}</p>
              <p className="text-sm text-[#50564F] flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1"><KeyRound className="w-4 h-4" /> {u.pin}</span>
                <span>· {u.role} · {langName(u.language || u.ui_lang)}</span>
              </p>
              {(u.email || u.phone) && (
                <p className="text-sm text-[#50564F] flex items-center gap-3 flex-wrap mt-0.5">
                  {u.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {u.email}</span>}
                  {u.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {u.phone}</span>}
                </p>
              )}
            </div>
            {!u.is_default && (
              <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="yb-tactile bg-[#DC2626] text-white p-2">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
